import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useHistory } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import Dialog from "veneer/Dialog";
import DropdownMenu from "veneer/DropdownMenu";
import MenuItem from "veneer/MenuItem";
import Stack from "veneer/Stack";
import {
  AppSettingsIcon,
  CameraVideoIcon,
  CancelledFilledIcon,
  EditIcon,
  FlagIcon,
  PowerRestartIcon,
  SettingsIcon,
} from "veneer/SvgIcon";
import Tabs, { Tab, TabRow } from "veneer/Tabs";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { useAppSelector } from "cortex/ui/src/hooks";
import {
  hasConnected,
  isSystemManaged,
  isTesting,
} from "cortex/ui/src/redux-store/devices/selectors";
import { networksSelector } from "cortex/ui/src/redux-store/networks/selectors";
import { isReadonly } from "cortex/ui/src/redux-store/users/selectors";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import WsDsConnStatus from "cortex/ui/src/ui/ws-ds-conn-status/WsDsConnStatus";
import { User } from "cortex/ui/src/user/model";
import { getBreadcrumbs } from "cortex/ui/src/util/breadcrumbs";
import type { ResponseLike } from "cortex/ui/src/util/response";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container, HeaderTabs, Panel } from "cortex/ui/src/view/layout";
import { VIEW } from "cortex/ui/src/view/views";

import DeviceVersionStatusChip from "./component/DeviceVersionStatusChip";
import { Device, DeviceStatus, Tag } from "./model";
import { formatOsBadge } from "./tableFormat";
import { DeviceTags } from "./tags";
import { canTakeWebcamSnapshotForDevice } from "./utils";

interface DeviceViewProps {
  activeTab?: string;
  children?: ReactNode;
  clearPlayerCache: (deviceId: number | string) => Promise<void>;
  clearWebcamSnapshotCache?: (deviceId: number | string) => Promise<void>;
  device: Device;
  deviceId: string;
  deviceStatus?: DeviceStatus;
  isThirdParty: boolean;
  loadDeviceStats: (deviceId: string) => Promise<void>;
  restartDevice: (deviceId: number | string) => Promise<void>;
  storesLoaded: boolean;
  unloadDeviceStats: () => void;
  user?: User;
}

interface DeviceViewState {
  activeDialogModal: DialogKey | null;
  errorMessage: string;
  loading: boolean;
  refreshing: boolean;
  restarting: boolean;
  successMessage: string;
}

const VISIBLE_TAGS = 2;

const DIALOGS = {
  CLEAR_CACHE: "clear-cache",
  CLEAR_WEBCAM_CACHE: "clear-webcam-cache",
  RESTART: "restart",
} as const;

type DialogKey = (typeof DIALOGS)[keyof typeof DIALOGS];

function renderBreadcrumbs() {
  return getBreadcrumbs([{ label: "Devices", target: "/devices" }]);
}

export default function DeviceViewPage({
  activeTab,
  children,
  clearPlayerCache,
  clearWebcamSnapshotCache,
  device,
  deviceId,
  deviceStatus,
  isThirdParty,
  loadDeviceStats,
  restartDevice,
  storesLoaded,
  unloadDeviceStats,
  user,
}: DeviceViewProps) {
  const history = useHistory();
  const isMountedRef = useRef(true);
  const networksById = useAppSelector(networksSelector);

  const [state, setState] = useState<DeviceViewState>({
    activeDialogModal: null,
    errorMessage: "",
    loading: false,
    refreshing: false,
    restarting: false,
    successMessage: "",
  });

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      unloadDeviceStats();
    };
  }, [unloadDeviceStats]);

  const readonly = useMemo(() => isReadonly(user), [user]);
  const canTakeWebcamSnapshot = useMemo(() => {
    return canTakeWebcamSnapshotForDevice(device, networksById);
  }, [device?.network_id, device?.user_flags, networksById]);

  const cleanTags = useMemo(() => {
    return (
      device?.tags?.filter(
        (tag): tag is Tag =>
          Boolean(tag?.id) &&
          Boolean(tag?.name) &&
          tag.name.length > 0 &&
          tag.name[0] !== "_",
      ) ?? []
    );
  }, [device?.tags]);

  const setPartialState = useCallback((updates: Partial<DeviceViewState>) => {
    if (!isMountedRef.current) {
      return;
    }

    setState((current) => ({ ...current, ...updates }));
  }, []);

  const triggerAction = useCallback(
    async (
      action: (id: number | string) => Promise<void>,
      successMessage: string,
      targetDeviceId: number | string,
    ) => {
      setPartialState({ loading: true });

      try {
        await action(targetDeviceId);
        setPartialState({
          activeDialogModal: null,
          errorMessage: "",
          loading: false,
          successMessage,
        });
      } catch (error) {
        setPartialState({
          activeDialogModal: null,
          errorMessage: getErrorMessage(error as Error | ResponseLike | null),
          loading: false,
        });
      }
    },
    [setPartialState],
  );

  const refresh = useCallback(async () => {
    if (!device) {
      return;
    }

    setPartialState({ refreshing: true });

    try {
      await loadDeviceStats(deviceId);
      setPartialState({
        errorMessage: "",
        refreshing: false,
      });
    } catch (error) {
      setPartialState({
        errorMessage: getErrorMessage(error as Error | ResponseLike | null),
        refreshing: false,
      });
    }
  }, [device, deviceId, loadDeviceStats, setPartialState]);

  useEffect(() => {
    if (device) {
      refresh();
    }
  }, [device, refresh]);

  const handleClearPlayerCache = useCallback(() => {
    if (readonly) {
      return;
    }

    setPartialState({ activeDialogModal: DIALOGS.CLEAR_CACHE });
  }, [readonly, setPartialState]);

  const handleClearWebcamCache = useCallback(() => {
    if (readonly) {
      return;
    }

    setPartialState({ activeDialogModal: DIALOGS.CLEAR_WEBCAM_CACHE });
  }, [readonly, setPartialState]);

  const handleRestartClick = useCallback(() => {
    if (readonly) {
      return;
    }

    setPartialState({ activeDialogModal: DIALOGS.RESTART });
  }, [readonly, setPartialState]);

  const handleErrorNotificationClose = useCallback(() => {
    setPartialState({ errorMessage: "" });
  }, [setPartialState]);

  const hideDialog = useCallback(() => {
    setPartialState({ activeDialogModal: null });
  }, [setPartialState]);

  const renderTags = useCallback((tags: ReadonlyArray<Tag>): ReactNode[] => {
    const tagsContent = <DeviceTags hideSystemTags key="tags" tags={tags} />;

    if (tags.length <= VISIBLE_TAGS) {
      return [tagsContent];
    }

    return [
      <Tooltip key="all-tags" title={tagsContent}>
        <JollyRancherChip color="magenta" label={`${tags.length} Tags`} />
      </Tooltip>,
    ];
  }, []);

  const badges = useMemo(() => {
    if (!device) {
      return [];
    }

    const items: ReactNode[] = [];

    if (device.os_platform) {
      items.push(formatOsBadge(device.os_platform));
    }

    items.push(<DeviceVersionStatusChip device={device} key="version" />);

    if (isSystemManaged(device)) {
      items.push(
        <JollyRancherChip
          color="red"
          key="is-synced"
          label="Ad Platform Synced"
        />,
      );
    }

    if (isTesting(device)) {
      items.push(
        <JollyRancherChip color="gray" key="testing" label="Testing" />,
      );
    } else {
      items.push(
        <JollyRancherChip color="teal" key="production" label="Production" />,
      );
    }

    if (device.network_id && device.network_name) {
      items.push(
        <Link key="network" to={`/networks/${device.network_id}`}>
          <JollyRancherChip color="blue" label={device.network_name} />
        </Link>,
      );
    }

    items.push(...renderTags(cleanTags));

    return items;
  }, [cleanTags, device, renderTags]);

  const renderDeviceActionConfirmation = useCallback(() => {
    if (!state.activeDialogModal || !device) {
      return null;
    }

    if (state.activeDialogModal === DIALOGS.CLEAR_CACHE) {
      return (
        <Dialog
          commitLabel="Clear Player Cache"
          onCancel={hideDialog}
          onCommit={() =>
            triggerAction(
              clearPlayerCache,
              "Clear cache operation initiated, please check back again shortly.",
              device.device_id ?? device.id,
            )
          }
          onDismiss={hideDialog}
          open
          title="Are you sure?"
          variant="modal"
        >
          This operation will clear the player cache.
        </Dialog>
      );
    }

    if (state.activeDialogModal === DIALOGS.CLEAR_WEBCAM_CACHE) {
      if (!clearWebcamSnapshotCache) {
        return null;
      }

      return (
        <Dialog
          commitLabel="Trigger New Webcam Snapshots"
          onCancel={hideDialog}
          onCommit={() =>
            triggerAction(
              clearWebcamSnapshotCache,
              "Player snapshots reset",
              device.device_id ?? device.id,
            )
          }
          onDismiss={hideDialog}
          open
          title="Are you sure?"
          variant="modal"
        >
          This operation will cause the player to take new webcam snapshots.
        </Dialog>
      );
    }

    if (state.activeDialogModal === DIALOGS.RESTART) {
      return (
        <Dialog
          commitLabel="Restart Device"
          onCancel={hideDialog}
          onCommit={() =>
            triggerAction(
              restartDevice,
              "The restart is initiated, please check back again shortly.",
              device.id,
            )
          }
          onDismiss={hideDialog}
          open
          title="Are you sure?"
          variant="modal"
        >
          This operation will restart the player. On ChromeOS and WebOS, the
          operating system will be rebooted.
        </Dialog>
      );
    }

    return null;
  }, [
    clearPlayerCache,
    clearWebcamSnapshotCache,
    device,
    deviceStatus,
    hideDialog,
    restartDevice,
    state.activeDialogModal,
    triggerAction,
  ]);

  const toolbox = useMemo(() => {
    const flagsTitle = readonly ? "View Flags" : "Edit Flags";
    const parameterTitle = readonly ? "View Parameters" : "Edit Parameters";

    return (
      <Stack
        alignItems="center"
        dataTestId="device-view-page-toolbox"
        direction="row"
        spacing={1}
      >
        <Button
          dataTestId="refresh-device"
          disabled={state.refreshing}
          icon="refresh"
          loading={state.refreshing}
          onClick={refresh}
          variant="outlined"
        >
          Refresh
        </Button>
        <DropdownMenu
          icon={<SettingsIcon size="small" />}
          label="Configure"
          variant="contained"
        >
          {!readonly && (
            <MenuItem
              dataTestId="edit-device"
              onClick={() => history.push(`/devices/${deviceId}/${VIEW.EDIT}`)}
            >
              <Stack alignItems="center" direction="row" spacing={4}>
                <EditIcon />
                <Typography>Edit Device</Typography>
              </Stack>
            </MenuItem>
          )}
          <MenuItem
            dataTestId="update-params"
            onClick={() => history.push(`/devices/${deviceId}/configure`)}
          >
            <Stack alignItems="center" direction="row" spacing={4}>
              <AppSettingsIcon />
              <Typography>{parameterTitle}</Typography>
            </Stack>
          </MenuItem>
          {!isThirdParty && (
            <MenuItem
              dataTestId="update-flags"
              onClick={() => history.push(`/devices/${deviceId}/flags`)}
            >
              <Stack alignItems="center" direction="row" spacing={4}>
                <FlagIcon />
                <Typography>{flagsTitle}</Typography>
              </Stack>
            </MenuItem>
          )}
          {canTakeWebcamSnapshot && (
            <MenuItem
              dataTestId="trigger-webcam-snapshots"
              onClick={handleClearWebcamCache}
            >
              <Stack alignItems="center" direction="row" spacing={4}>
                <CameraVideoIcon />
                <Typography>Trigger New Webcam Snapshots</Typography>
              </Stack>
            </MenuItem>
          )}
          {!isThirdParty && (
            <MenuItem
              dataTestId="clear-player-cache"
              onClick={handleClearPlayerCache}
            >
              <Stack alignItems="center" direction="row" spacing={4}>
                <CancelledFilledIcon />
                <Typography>Clear Player Cache</Typography>
              </Stack>
            </MenuItem>
          )}
          {!isThirdParty && (
            <MenuItem dataTestId="restart-device" onClick={handleRestartClick}>
              <Stack alignItems="center" direction="row" spacing={4}>
                <PowerRestartIcon />
                <Typography>Restart Device</Typography>
              </Stack>
            </MenuItem>
          )}
        </DropdownMenu>
      </Stack>
    );
  }, [
    deviceId,
    canTakeWebcamSnapshot,
    handleClearPlayerCache,
    handleClearWebcamCache,
    handleRestartClick,
    history,
    isThirdParty,
    readonly,
    refresh,
    state.refreshing,
  ]);

  const notification =
    state.errorMessage || state.successMessage ? (
      <Notification
        error={state.errorMessage}
        onErrorClose={handleErrorNotificationClose}
        success={state.successMessage}
      />
    ) : null;

  const tabs = (
    <Tabs key={`tabs-${activeTab}`}>
      <Tab
        defaultSelected={activeTab === "overview"}
        label="Overview"
        onClick={() => history.push(`/devices/${deviceId}/overview`)}
      />
      <Tab
        defaultSelected={activeTab === "events"}
        label="Events"
        onClick={() => history.push(`/devices/${deviceId}/events`)}
      />
      {!isThirdParty && (
        <Tab
          defaultSelected={activeTab === "details"}
          label="Details"
          onClick={() => history.push(`/devices/${deviceId}/details`)}
        />
      )}
      <Tab
        defaultSelected={activeTab === "internals"}
        label="Internal State"
        onClick={() => history.push(`/devices/${deviceId}/internals`)}
      />
      <Tab
        defaultSelected={activeTab === "logs"}
        label="Logs"
        onClick={() => history.push(`/devices/${deviceId}/logs`)}
      />
    </Tabs>
  );

  const connectedContent = (
    <>
      <WsDsConnStatus />
      {notification}

      <HeaderTabs>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <TabRow />
          <Stack alignItems="center" direction="row" spacing={1}>
            {badges}
          </Stack>
        </Box>

        {tabs}
      </HeaderTabs>

      {children}
      {renderDeviceActionConfirmation()}
    </>
  );

  const content = !storesLoaded ? (
    <NavLoader.Proxy />
  ) : !hasConnected(device) ? (
    <Panel>
      <Alert dataTestId="device-not-connected-alert" severity="info">
        This device has been registered, but it has not connected to our systems
        yet.
      </Alert>
    </Panel>
  ) : (
    connectedContent
  );

  return (
    <Container
      breadcrumbs={renderBreadcrumbs()}
      desc={deviceId}
      hideBox
      title={device?.name ?? ""}
      toolbox={device ? toolbox : null}
    >
      {content}
    </Container>
  );
}
